/**
 * Notion-style 3-zone drop detection.
 *
 * For folders (non-leaf nodes):
 *   - top 25% → 'before' (insert above)
 *   - middle 50% → 'on' (move into)
 *   - bottom 25% → 'after' (insert below)
 *
 * For leaf nodes (artifacts without children) there is no "into" zone:
 *   - top half → 'before'
 *   - bottom half → 'after'
 *
 * Exported for unit tests.
 */
export function getDropPosition(relY: number, isLeaf: boolean): 'before' | 'after' | 'on' {
  if (relY < 0.25 || (isLeaf && relY < 0.5)) return 'before';
  if (relY > 0.75 || (isLeaf && relY >= 0.5)) return 'after';
  return 'on';
}

export type DropPosition = 'before' | 'after' | 'on';

/**
 * The tree drag's `dataTransfer` type.
 *
 * Deliberately private, mirroring `application/x-bower-table-drag`. A drag is
 * document-scoped: every drop listener on the page reads the same store, so a
 * `text/plain` payload is an instruction to anything with default handling for
 * it. Keep any future tree-drag payload on this type.
 */
export const TREE_NODE_DRAG_MIME = 'application/x-bower-tree-node';

/**
 * One dragged item, as the tree and the workspace canvas both write it under
 * `TREE_NODE_DRAG_MIME`, so a drag started in either lands in the other.
 * `parentId` is where the item is now (null = the workspace root); a move's
 * undo sends it back there. `artifactType` (files only) is what a drop on the
 * chat composer needs to name the item as context; a move never reads it.
 */
export interface DraggedItem {
  readonly id: string;
  readonly kind: 'folder' | 'file';
  readonly parentId: string | null;
  readonly name: string;
  readonly artifactType?: string;
}

/** Whether a drag carries items (tree nodes or canvas items). Readable during dragover. */
export function isItemDrag(dataTransfer: DataTransfer | null): boolean {
  return !!dataTransfer && Array.from(dataTransfer.types ?? []).includes(TREE_NODE_DRAG_MIME);
}

/** Whether a drag carries files from the operating system (never an item drag or text). */
export function isFileDrag(dataTransfer: DataTransfer | null): boolean {
  return !!dataTransfer && Array.from(dataTransfer.types ?? []).includes('Files');
}

export function writeItemDragPayload(
  dataTransfer: DataTransfer,
  items: ReadonlyArray<DraggedItem>,
): void {
  dataTransfer.setData(TREE_NODE_DRAG_MIME, JSON.stringify(items));
}

const isDraggedItem = (v: unknown): v is DraggedItem => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    (o.kind === 'folder' || o.kind === 'file') &&
    (o.parentId === null || typeof o.parentId === 'string') &&
    typeof o.name === 'string' &&
    (o.artifactType === undefined || typeof o.artifactType === 'string')
  );
};

/**
 * The dragged items, readable on drop only (the store is protected before
 * that). Anything that is not a well-formed list is no items at all: a drag
 * can come from another window, so the payload is input, not trusted state.
 */
export function readItemDragPayload(dataTransfer: DataTransfer | null): DraggedItem[] {
  const raw = dataTransfer?.getData(TREE_NODE_DRAG_MIME);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every(isDraggedItem) ? parsed : [];
  } catch {
    return [];
  }
}
